// a context that holds the state of the project and the functions that change the state

import { createContext, useContext, useReducer } from 'react';
import { get as getProjection } from 'ol/proj.js';
import { getFormat } from '@utils/map/helpers';
import { PROJECT_MODE, IMAGE_MODE } from '@utils/constants';

const ProjectContext = createContext(null);

const defaultProjection = getProjection('EPSG:3857');
const defaultFormat = getFormat(defaultProjection);

// Default color options
const colorOptions = [
	{ r: 237, g: 100, b: 203, a: 1 },
	{ r: 0, g: 225, b: 255, a: 1 },
	{ r: 244, g: 109, b: 67, a: 1 },
	{ r: 148, g: 137, b: 255, a: 1 },
	{ r: 114, g: 247, b: 255, a: 1 },
	{ r: 158, g: 1, b: 66, a: 1 },
	{ r: 230, g: 245, b: 152, a: 1 },
	{ r: 171, g: 221, b: 164, a: 1 },
	{ r: 255, g: 255, b: 255, a: 1 },
	{ r: 182, g: 138, b: 96, a: 1 },
];

const colorScheme = colorOptions.reduce((acc, color, index) => {
	acc[index] = { color, visible: true };
	return acc;
}, {});

// Available draw tools grouped in annotation types
const availableAnnotationDrawTools = {
	object_detection: ['Box', 'Circle'],
	segmentation: ['Polygon', 'Freehand'],
};

const initialState = {
	project: null,
	projectMode: null,
	isDemo: false,

	defaultProjection,
	defaultFormat,
	colorOptions,
	colorScheme,

	/** Array of properties and geometries for each detection */
	detections: [],

	/** Object with feature arrays by their name */
	features: {},

	trainingDataClassNameInfo: {},

	singleImageFeatures: [],
	selectedSingleImage: null,
	activeFilename: null,
	filteredImages: null,

	mapObject: null,
	mapTooltip: null,

	measurementLayer: null,
	hexagonLayer: null,

	pickedTask: null,
	pickedModelClasses: null,
	tasks: null,
	dialogue: null,
	toolBarVisible: true,
	singleImageSliderOpen: true,
	interactionsDisabled: false,
	measurementLayerAdded: false,

	hexagonInfo: null,
	hexagonColor: { color: '0, 0, 128' },
	hexagonSize: 1000,
	selectedHexagon: null,

	dspFeaturesInfo: null,

	extent: null,

	annotationMode: null,
	annotationSidebarData: null,
	annotationDrawTool: null,
	modelType: null,
	isRotateEnabled: false,

	confirmModalContent: null,

	activeSidebars: [{ sidebarId: 'layerView' }],
	modifyLayersSidebarRef: null,

	// This is to save map view extent to reset view after annotation.
	// Annotation is re-rendering so many components that may or may not save original state before annotation.
	// This causes selectedSingleImage to bug out unless reset to null, and causes map layer visibility to not save.
	// This is expected to be fixed when annotation tools are remade.
	savedMapExtent: null,
};

// Helper function to generate state updater
const createStateUpdater = key => (state, payload) => ({
	...state,
	[key]: payload,
});

const actions = {
	// Simple state updates
	setDetections: createStateUpdater('detections'),
	setFeatures: createStateUpdater('features'),
	setSingleImageFeatures: createStateUpdater('singleImageFeatures'),
	setFilteredImages: createStateUpdater('filteredImages'),
	setSelectedSingleImage: createStateUpdater('selectedSingleImage'),
	setActiveFilename: createStateUpdater('activeFilename'),
	setColorScheme: createStateUpdater('colorScheme'),
	setTrainingDataClassNameInfo: createStateUpdater('trainingDataClassNameInfo'),
	setMapObject: createStateUpdater('mapObject'),
	setMapTooltip: createStateUpdater('mapTooltip'),
	setMeasurementLayer: createStateUpdater('measurementLayer'),
	setHexagonLayer: createStateUpdater('hexagonLayer'),
	setPickedTask: createStateUpdater('pickedTask'),
	setPickedModelClasses: createStateUpdater('pickedModelClasses'),
	setTasks: createStateUpdater('tasks'),
	setDialogue: createStateUpdater('dialogue'),
	setToolBarVisible: createStateUpdater('toolBarVisible'),
	setSingleImageSliderOpen: createStateUpdater('singleImageSliderOpen'),
	setInteractionsDisabled: createStateUpdater('interactionsDisabled'),
	setMeasurementLayerAdded: createStateUpdater('measurementLayerAdded'),
	setHexagonInfo: createStateUpdater('hexagonInfo'),
	setHexagonColor: createStateUpdater('hexagonColor'),
	setHexagonSize: createStateUpdater('hexagonSize'),
	setSelectedHexagon: createStateUpdater('selectedHexagon'),
	setExtent: createStateUpdater('extent'),
	setIsDemo: createStateUpdater('isDemo'),
	setAnnotationMode: createStateUpdater('annotationMode'),
	setAnnotationSidebarData: createStateUpdater('annotationSidebarData'),
	setConfirmModalContent: createStateUpdater('confirmModalContent'),
	setActiveSidebars: createStateUpdater('activeSidebars'),
	setDspFeaturesInfo: createStateUpdater('dspFeaturesInfo'),
	setModifyLayersSidebarRef: createStateUpdater('modifyLayersSidebarRef'),
	setSavedMapExtent: createStateUpdater('savedMapExtent'),
	setIsRotateEnabled: createStateUpdater('isRotateEnabled'),
};

const setProjectMode = image_mode => {
	switch (image_mode) {
		case IMAGE_MODE.GENERATE_ORTHOPHOTO:
		case IMAGE_MODE.UPLOAD_ORTHOPHOTO:
			return PROJECT_MODE.ORTHOPHOTO;
		case IMAGE_MODE.SINGLE_IMAGE:
			return PROJECT_MODE.SINGLE_IMAGE;
		default:
			// Default is orthophoto
			return PROJECT_MODE.ORTHOPHOTO;
	}
};

function projectReducer(state, action) {
	// Handle different action types using a switch statement
	switch (action.type) {
		case 'setProject':
			// If the payload is null, reset the project and projectMode to null
			if (action.payload === null) {
				return { ...state, project: null, projectMode: null };
			}
			// Determine the project mode based on the image mode in the payload
			const projectMode = setProjectMode(action.payload.image_mode);
			// Update the state with the new project and project mode
			return { ...state, project: action.payload, projectMode };

		case 'setModelType':
			try {
				// Get the default draw tool for the given annotation type
				const defaultDrawTool = availableAnnotationDrawTools[action.payload][0];
				// Update the state with the new annotation type and draw tool
				return {
					...state,
					modelType: action.payload,
					annotationDrawTool: defaultDrawTool,
				};
			} catch (error) {
				// Log an error if there is an issue setting the annotation type
				console.warn('Error setting model type:', error);
				// Update the state with the new annotation type without changing the draw tool
				return { ...state, modelType: action.payload };
			}

		case 'setAnnotationDrawTool':
			if (action.payload === null && !!state.modelType) {
				return {
					...state,
					annotationDrawTool: availableAnnotationDrawTools[state.modelType][0],
				};
			}
			// Update the state with the new draw tool
			return { ...state, annotationDrawTool: action.payload };

		case 'resetState':
			// Reset the state to the initial state
			return initialState;

		default:
			// Handle custom actions defined in the actions object
			const handler = actions[action.type];
			if (!handler) {
				// Throw an error if the action type is unknown
				throw new Error(`Unknown action: ${action.type}`);
			}
			// Call the custom handler with the current state and action payload
			return handler(state, action.payload);
	}
}

export function ProjectProvider({ children }) {
	// How to use:
	// `dispatch({ type: 'setProject', payload: newProject }).`
	const [state, dispatch] = useReducer(projectReducer, initialState);

	const setActiveSidebar = ({ sidebarId, parentId }) => {
		const sidebarElement = { sidebarId, parentId };

		// Remove any other active sidebar that is of same parent
		const newActiveSidebars = state.activeSidebars;

		// Check if sidebarId already exists
		const doesExist = newActiveSidebars.some(sidebar => sidebar.sidebarId === sidebarId);

		// If sidebarId does not exist, add the new sidebarElement
		if (!doesExist) {
			newActiveSidebars.push(sidebarElement);
			dispatch({ type: 'setActiveSidebars', payload: newActiveSidebars });
		}
	};

	const removeFromActiveSidebars = sidebarId => {
		dispatch({
			type: 'setActiveSidebars',
			payload: state.activeSidebars.filter(sidebar => sidebar.sidebarId !== sidebarId),
		});
	};

	return (
		<ProjectContext.Provider
			value={{
				...state,
				dispatch,
				availableAnnotationDrawTools,
				setActiveSidebar,
				removeFromActiveSidebars,
			}}>
			{children}
		</ProjectContext.Provider>
	);
}

export function useProject() {
	const context = useContext(ProjectContext);
	if (!context) throw new Error('useProject must be used within a ProjectProvider');
	return context;
}
